import axios from 'axios';

// Environment-aware API configuration
const getApiBaseUrl = () => {
  // Check for explicit API URL first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Auto-detect based on current hostname
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // If running on localhost (frontend port 3000), use backend port 5000
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Frontend runs on port 3000, backend on port 5000
    return 'http://localhost:3000/api';
  }

  // For production/server environments, use the same host
  const baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  return `${baseUrl}/api`;
};

const API_BASE_URL = getApiBaseUrl();
console.log('API Base URL:', API_BASE_URL);
console.log('Current location:', window.location.href);
console.log('Hostname:', window.location.hostname);
console.log('Port:', window.location.port);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Forms API
export const formsAPI = {
  // Get all forms
  getAll: () => api.get('/forms'),

  // Get a specific form (public access)
  getById: (id) => api.get(`/forms/${id}`),
  get: (id) => api.get(`/forms/${id}`),

  // Create a new form
  create: (formData) => api.post('/forms', formData),

  // Duplicate an existing form (optional body: { title, folder_id })
  duplicate: (id, data = {}) => api.post(`/forms/${id}/duplicate`, data),

  // Update a form
  update: (id, formData) => api.put(`/forms/${id}`, formData),

  // Delete a form
  delete: (id) => api.delete(`/forms/${id}`),

  // Get form analytics
  getAnalytics: (id) => api.get(`/forms/${id}/analytics`),

  // Get form by custom URL (encode to handle reserved characters like &)
  getByCustomUrl: (customUrl) => api.get(`/forms/custom/${encodeURIComponent(customUrl)}`),

  // Get shared form (public access)
  getShared: (id) => api.get(`/forms/shared/${id}`),

  // Verify password for a form
  verifyPassword: (id, password) => api.post(`/forms/${id}/verify-password`, { password }),

  // Export form as Word (.doc)
  exportWord: (id) => api.get(`/forms/export/word/${id}`, { responseType: 'blob' }),
};

// Templates API
export const templatesAPI = {
  // Get all templates
  getAll: () => api.get('/templates'),

  // Get a specific template
  getById: (id) => api.get(`/templates/${id}`),

  // Create a form from a template
  useTemplate: (id, formData) => api.post(`/templates/${id}/use`, formData),
};

// Responses API
export const responsesAPI = {
  // Track form view
  trackView: (formId, sessionId) => api.post('/responses/view', { form_id: formId, session_id: sessionId }),

  // Per-option submission counts for a dropdown (public; field must have showOptionBookingCounts)
  getOptionCounts: (formId, fieldId) =>
    api.get(`/responses/option-counts/${formId}`, { params: { field_id: fieldId } }),

  // Submit a response
  submit: (responseData) => api.post('/responses', responseData),

  // Get responses for a form
  getByForm: (formId, page = 1, limit = 10) =>
    api.get(`/responses/form/${formId}?page=${page}&limit=${limit}`),

  // Get a specific response
  getById: (id) => api.get(`/responses/${id}`),

  // Get response analytics
  getAnalytics: (params = {}) => api.get('/responses/analytics/summary', { params }),

  // Get view tracking data for a form
  getViewTracking: (formId) => api.get(`/responses/views/${formId}`),

  // Export responses as CSV
  exportCSV: (formId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('date_from', filters.startDate);
    if (filters.endDate) params.append('date_to', filters.endDate);

    const queryString = params.toString();
    const url = `/responses/export/csv/${formId}${queryString ? `?${queryString}` : ''}`;

    return api.get(url, {
      responseType: 'blob',
    });
  },

  // Export responses as JSON
  exportJSON: (formId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('date_from', filters.startDate);
    if (filters.endDate) params.append('date_to', filters.endDate);

    const queryString = params.toString();
    const url = `/responses/export/json/${formId}${queryString ? `?${queryString}` : ''}`;

    return api.get(url, {
      responseType: 'blob',
    });
  },

  // Export responses as Excel
  exportExcel: (formId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('date_from', filters.startDate);
    if (filters.endDate) params.append('date_to', filters.endDate);

    const queryString = params.toString();
    const url = `/responses/export/excel/${formId}${queryString ? `?${queryString}` : ''}`;

    return api.get(url, {
      responseType: 'blob',
    });
  },

  // Export responses as PDF
  exportPDF: (formId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('date_from', filters.startDate);
    if (filters.endDate) params.append('date_to', filters.endDate);

    const queryString = params.toString();
    const url = `/responses/export/pdf/${formId}${queryString ? `?${queryString}` : ''}`;

    console.log('PDF Export - Form ID:', formId);
    console.log('PDF Export - Filters:', filters);
    console.log('PDF Export - Full URL:', `${API_BASE_URL}${url}`);

    return api.get(url, {
      responseType: 'blob',
    });
  },

  // Delete a response
  delete: (responseId) => api.delete(`/responses/${responseId}`),
};

// Authentication API
export const authAPI = {
  // Login
  login: (username, password) => api.post('/auth/login', { username, password }),

  // Register (admin only)
  register: (username, email, password, role, token) =>
    api.post('/auth/register', { username, email, password, role }, {
      headers: { Authorization: `Bearer ${token}` }
    }),

  // Get current user profile
  getProfile: () => api.get('/auth/profile'),

  // Microsoft Authentication
  getMicrosoftLoginUrl: () => api.get('/auth/microsoft/login-url'),
  microsoftCallback: (code) => api.post('/auth/microsoft/callback', { code }),
};

// Folders API
export const foldersAPI = {
  // Get all folders
  getFolders: () => api.get('/folders'),

  // Get a specific folder with its forms
  getFolder: (folderId) => api.get(`/folders/${folderId}`),

  // Create a new folder
  createFolder: (folderData) => api.post('/folders', folderData),

  // Update a folder
  updateFolder: (folderId, folderData) => api.put(`/folders/${folderId}`, folderData),

  // Delete a folder
  deleteFolder: (folderId) => api.delete(`/folders/${folderId}`),

  // Move forms to a folder
  moveFormsToFolder: (folderId, formIds) => api.post(`/folders/${folderId}/move-forms`, { form_ids: formIds }),

  // Get a shared folder (public access)
  getSharedFolder: (folderId) => api.get(`/folders/shared/${folderId}`),
};

// Users API
export const usersAPI = {
  // Get all users
  getAll: () => api.get('/users'),

  // Get user roles
  getRoles: () => api.get('/users/roles/list'),

  // Create a new user
  create: (userData) => api.post('/users', userData),

  // Update a user
  update: (id, userData) => api.put(`/users/${id}`, userData),

  // Delete a user
  delete: (id) => api.delete(`/users/${id}`),
};

// Upload API
export const uploadAPI = {
  // Upload logo image
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post('/upload/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Get logo URL
  getLogoUrl: (filename) => {
    // Prefer the API endpoint for serving logos; works behind reverse proxies
    // Example: https://yourdomain.com/api/upload/logos/:filename
    return `${API_BASE_URL}/upload/logos/${filename}`;
  },

  // Delete logo
  deleteLogo: (filename) => api.delete(`/upload/logos/${filename}`),
};

// Sharing API
export const sharingAPI = {
  // Share form with users
  shareForm: (formId, users, permissionType) =>
    api.post(`/sharing/form/${formId}/share`, { users, permissionType }),

  // Get users who have access to a form
  getFormSharedUsers: (formId) =>
    api.get(`/sharing/form/${formId}/shared-users`),

  // Remove user access from form
  removeFormAccess: (formId, userId) =>
    api.delete(`/sharing/form/${formId}/share/${userId}`),

  // Share folder with users
  shareFolder: (folderId, users, permissionType) =>
    api.post(`/sharing/folder/${folderId}/share`, { users, permissionType }),

  // Get users who have access to a folder
  getFolderSharedUsers: (folderId) =>
    api.get(`/sharing/folder/${folderId}/shared-users`),

  // Remove user access from folder
  removeFolderAccess: (folderId, userId) =>
    api.delete(`/sharing/folder/${folderId}/share/${userId}`),
};

// Certificates API
export const certificatesAPI = {
  // Generate certificate for questionnaire completion
  generateCertificate: (formId, responseId) =>
    api.post(`/certificates/generate`, { form_id: formId, response_id: responseId }),

  // Get certificate by ID
  getCertificate: (certificateId) =>
    api.get(`/certificates/${certificateId}`),

  // Get certificate PDF
  getCertificatePDF: (certificateId) =>
    api.get(`/certificates/pdf/${certificateId}`, { responseType: 'blob' }),

  // Track certificate download
  trackDownload: (certificateId, downloadType = 'pdf') =>
    api.post(`/certificates/track-download`, { certificate_id: certificateId, download_type: downloadType }),

  // Get certificate downloads for a form
  getCertificateDownloads: (formId) =>
    api.get(`/certificates/downloads/${formId}`)
};

// AI API
export const aiAPI = {
  // Generate form from prompt
  generateForm: (prompt) => api.post('/ai/generate-form', { prompt }),

  // Convert Word document to questionnaire
  convertWordToQuestionnaire: (file) => {
    const formData = new FormData();
    formData.append('wordFile', file);
    return api.post('/ai/convert-word-to-questionnaire', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// QR Codes API
export const qrcodesAPI = {
  // Get all QR codes for the authenticated user
  getAll: (folderId) => {
    const params = folderId ? { folder_id: folderId } : {};
    return api.get('/qrcodes', { params });
  },

  // Get a specific QR code by ID
  getById: (id) =>
    api.get(`/qrcodes/${id}`),

  // Create a new QR code
  create: (data) =>
    api.post('/qrcodes', data),

  // Update QR code
  update: (id, data) =>
    api.put(`/qrcodes/${id}`, data),

  // Update QR code content (for dynamic QR codes)
  updateContent: (id, content, qrType) =>
    api.put(`/qrcodes/${id}/content`, { content, qr_type: qrType }),

  // Move QR code to folder
  moveToFolder: (id, folderId) =>
    api.put(`/qrcodes/${id}/move`, { folder_id: folderId }),

  // Delete QR code
  delete: (id) =>
    api.delete(`/qrcodes/${id}`)
};

// Health check
export const healthCheck = () => api.get('/health');

export default api;
